import {useState, useEffect, useCallback} from 'react'
import {useHeaderSizeWithoutBanner} from './variants/without-banner'
import {
    HEADER_SIZE_COLLAPSED_WITHOUT_BANNER,
    HEADER_SIZE_COLLAPSED_WITH_BANNER,
    HEADER_SIZE_NOT_COLLAPSED_WITHOUT_BANNER,
    HEADER_SIZE_NOT_COLLAPSED_WITH_BANNER,
    addAccessibilityBannerHeight,
    ACCESSIBILITY_BANNER_SIZE,
} from '_utils'
import {debounce} from 'lodash'
import type {ThemeBreakpoints} from '_uiComponents'
import {useAccessibilityState} from '_context'

/**
 * @name useHeaderSize
 *
 * @hook
 *
 * @description
 * Custom hook that calculates the height of the header.
 *
 * @remarks
 * This size includes the banner height.
 *
 * @example
 * ```tsx
 * const {headerSize} = useHeaderSize();
 * const styles = useMultiStyleConfig('Sample', {headerSize}) as Record<string, any>
 * ```
 *
 * @example with `useScrollAtTop` custom hook usage
 * ```tsx
 * const isAtTop = useScrollAtTop()
 * const {headerSize} = useHeaderSize(!isAtTop)
 * const styles = useMultiStyleConfig('Sample', {headerSize}) as Record<string, any>
 * ```
 *
 * @param {boolean | undefined} isCollapsed - Indicates if the user has scrolled down from the top and the header has collapsed.
 *
 * @returns {{ headerSize: ThemeBreakpoints, accessibilityBannerSize: ThemeBreakpoints }} Object with header size and accessibility banner size in number values for each available breakpoint.
 */
const useHeaderSize = (isCollapsed: boolean = false): {[key: string]: ThemeBreakpoints} => {
    const {isAccessibilityBannerEnabled} = useAccessibilityState()
    let headerSizeValue = HEADER_SIZE_COLLAPSED_WITH_BANNER
    if (!isCollapsed) {
        headerSizeValue = HEADER_SIZE_NOT_COLLAPSED_WITH_BANNER
        if (isAccessibilityBannerEnabled) {
            headerSizeValue = addAccessibilityBannerHeight(headerSizeValue)
        }
    }

    const [headerSize, setHeaderSize] = useState<ThemeBreakpoints>(headerSizeValue)

    /**
     * Helper function to calculate header size based on banner height.
     * @param {ThemeBreakpoints} baseSize - The base size of the header.
     * @param {number} bannerSize - The size of the banner.
     * @returns {ThemeBreakpoints} - The calculated header size.
     */
    const calculateHeaderSize = (baseSize: ThemeBreakpoints, bannerSize: number, isAccessibilityBannerEnabled: boolean): ThemeBreakpoints => {
        let headerSizeValue = {
            base: baseSize.base + bannerSize,
            sm: baseSize.sm + bannerSize,
            md: baseSize.md + bannerSize,
            lg: baseSize.lg + bannerSize,
            xl: baseSize.xl + bannerSize,
            '2xl': baseSize['2xl'] + bannerSize,
        }
        if (isAccessibilityBannerEnabled) {
            headerSizeValue = addAccessibilityBannerHeight(headerSizeValue)
        }

        return headerSizeValue
    }

    /**
     * Re-calculates the header size based on the banner height.
     * @remarks Careful to use the `WITHOUT_BANNER` version here.
     * @param {number} bannerSize Banner size.
     */
    const handleHeaderSize = useCallback(
        (bannerSize: number) => {
            if (!isCollapsed) {
                setHeaderSize(calculateHeaderSize(HEADER_SIZE_NOT_COLLAPSED_WITHOUT_BANNER, bannerSize, isAccessibilityBannerEnabled))
            } else {
                setHeaderSize(calculateHeaderSize(HEADER_SIZE_COLLAPSED_WITHOUT_BANNER, bannerSize, isAccessibilityBannerEnabled))
            }
        },
        [isCollapsed],
    )

    useEffect(() => {
        const bannerElement = window.document.querySelector('[data-testid="banner-header"]') as HTMLElement

        const observer = new MutationObserver(() => {
            if (bannerElement) {
                handleHeaderSize(bannerElement.offsetHeight)
                observer.disconnect()
            }
        })

        if (bannerElement) {
            observer.observe(bannerElement, {attributes: true, childList: true, subtree: true})
        } else {
            handleHeaderSize(0)
        }

        return () => {
            if (bannerElement) {
                observer.disconnect()
            }
        }
    }, [handleHeaderSize])

    const getBannerSize = (): number => {
        const banner = window.document.querySelector('[data-testid="banner-header"]') as HTMLElement
        const bannerSize = banner?.offsetHeight ?? 0
        return bannerSize
    }

    const handleResize = useCallback(() => {
        handleHeaderSize(getBannerSize())
    }, [handleHeaderSize])

    const debouncedHandleResize = useCallback(debounce(handleResize, 200), [handleResize])

    useEffect(() => {
        window.addEventListener('resize', debouncedHandleResize)
        return () => {
            window.removeEventListener('resize', debouncedHandleResize)
        }
    }, [debouncedHandleResize])

    /**
     * Setup header size after 500ms to wait proper render of `banner` component.
     */
    useEffect(() => {
        setTimeout(() => {
            handleHeaderSize(getBannerSize())
        }, 1000)
    }, [])

    return {headerSize, accessibilityBannerSize: (isAccessibilityBannerEnabled ? ACCESSIBILITY_BANNER_SIZE : {base: 0}) as ThemeBreakpoints}
}

export {useHeaderSize}
export {useHeaderSizeWithoutBanner}
